import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus } from 'lucide-react';
import useApi from '../../../hooks/useApi';
import Message from '../../../components/Message';
import Pagination from '../../../components/Pagination';
import { getUserList, sendInvitation } from '../../../services/invitationsService';
import { useToast } from '../../../contexts/ToastContext';


const InvitationsPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState({});
  const [invitationStats, setInvitationStats] = useState({
    accepted: 0,
    rejected: 0,
    sent: 0,
    pending: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });

  // const [loading, setLoading] = useState(false);

  // API hooks
  const { loading, error, callApi: fetchUsersApi } = useApi(getUserList, {showSuccessToast: false});
  const { loading: sendingInvitation, error: invitationError, callApi: sendInvitationApi } = useApi(sendInvitation, {showSuccessToast: true, msg: "Invitation sent successfully"});
  const [invitationSuccessMessage, setInvitationSuccessMessage] = useState(null);

  const fetchUsers = useCallback(async () => {
    const data = await fetchUsersApi(pagination.page, pagination.page_size, searchTerm);
    if (data) {
      setUsers(data.results);
      setInvitationStats(data.invitation_stats);
      setPagination(prev => ({
        ...prev,
        total_pages: data.total_pages,
        count: data.count
      }));
    }
  }, [fetchUsersApi, pagination.page, pagination.page_size, searchTerm]);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const debounceTimer = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [fetchUsers]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handlePermissionChange = (userId, permission) => {
    setSelectedPermission(prev => ({ ...prev, [userId]: permission }));
  };

  const handleSendInvitation = async (userId) => {
    const permissionLevel = selectedPermission[userId];
    if (!permissionLevel) {
      showError('Please select a permission level.')
      return;
    }

    const invitationData = {
      recipient: userId,
      permission_level: permissionLevel,
    };

    try {
      await sendInvitationApi(invitationData);
      
      // Refresh users list to update stats
      fetchUsers();
      
      // setInvitationSuccessMessage('Invitation sent successfully!');
      setTimeout(() => setInvitationSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error sending invitation:", err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collaborators</h1>
          <p className="text-gray-600">Manage and invite collaborators</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {invitationSuccessMessage && (
        <Message type="success" message={invitationSuccessMessage} />
      )}
      {invitationError && (
        <Message type="error" message={invitationError} />
      )}

      {/* Main Navigation Buttons */}
      <div className="flex flex-wrap gap-3">
        <button 
          onClick={() => navigate('/dashboard/invitations/received')}
          className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
        >
          Received Invitations
        </button>
        <button 
          onClick={() => navigate('/dashboard/invitations/sent')}
          className="inline-flex items-center px-6 py-3 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Sent Invitations
        </button>
      </div>

      {/* Search and User List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Search users..." 
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
              }}
            />
          </div>
        </div>

        {/* User List */}
        <div className="mt-4">
          {loading ? (
            <p className="text-center py-4">Loading users...</p>
          ) : error ? (
            <Message type="error" message={error} />
          ) : users.length === 0 ? (
            <p className="text-center py-4 text-gray-500">No users found</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {users.map(user => (
                <li key={user.id} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{user.username}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      className="border border-gray-300 rounded-lg text-sm p-2"
                      value={selectedPermission[user.id] || ''}
                      onChange={(e) => handlePermissionChange(user.id, e.target.value)}
                    >
                      <option value="">Select Permission</option>
                      <option value="view">View</option>
                      <option value="view_download">View & Download</option>
                      <option value="full_management">Full Management</option>
                    </select>
                    <button
                      className="inline-flex items-center px-3 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                      onClick={() => handleSendInvitation(user.id)}
                      disabled={!selectedPermission[user.id] || sendingInvitation}
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      {sendingInvitation ? 'Sending...' : 'Invite'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.total_pages}
        totalItems={pagination.count}
        pageSize={pagination.page_size}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        itemName="Users"
      />
    </div>
  );
};

export default InvitationsPage;